// The opening message handed to the workflow agent for a brand-new project.
//
// Extracted from `ProjectPage`'s `pendingKickoff` effect so the two surfaces that
// start a project cannot drift on it: `/learn` still runs it through the BROWSER
// round loop (`runBackgroundWorkflowTurn`, because `handleProblemStatementConfirm`
// has already stamped the template locally), while the dashboard composer sends
// the very same text to the SERVER run. The blank-canvas wording in particular is
// a fixed regression, not a preference — see below — and a second copy of it is
// exactly where that would be undone.

/**
 * @param context        the user's problem statement, attachment lines folded in
 * @param templateLabel  the stamped template's name, or null for a blank canvas
 * @param moduleLabels   labels of the modules the template created
 * @param firstLabel     the module to start with
 */
export function buildKickoffMessage({
  context,
  templateLabel = null,
  moduleLabels = [],
  firstLabel = null,
} = {}) {
  let msg = `I'm starting a new project. Here's the context for what I'm trying to solve:\n\n${context}`;
  if (templateLabel) {
    msg += `\n\nI've already added the ${templateLabel} template, which created these modules: ${moduleLabels.join(', ')}.`;
    if (firstLabel) {
      msg += ` Please confirm your understanding of my problem, then activate the first module ("${firstLabel}") and guide me through filling it out.`;
    }
    return msg;
  }
  // BLANK CANVAS — the composer's only path (it always sends a null templateKey),
  // so this is nearly every new project.
  //
  // Deliberately INTENT, not procedure. This text is shown to the user as their own
  // opening message, and it used to be a checklist — "state the sequence of modules
  // you're going to set up, create every module, connect every one of them, start the
  // first module" — which read as the app talking to itself and, worse, handed the
  // agent the STEPS instead of the GOAL. Those steps already live in the
  // orchestrator's system prompt ("do ALL of this in one turn"), so stating them here
  // bought nothing and crowded out the one thing only the user can supply: the result
  // they want. The prompt is also where the METHOD belongs — work back from the
  // result to the modules that produce it.
  //
  // The opposite failure is still live and must not be reintroduced: an opening that
  // said only "help me get started" got the pipeline half-built — tiles created,
  // nothing wired, nothing started. So this still asks for a workflow that RUNS; it
  // just no longer dictates how to arrive at one.
  //
  // It used to end "don't ask me to confirm each step", which forbade the ONE round
  // of questions that now precedes the plan — and the agent obeyed it by inventing
  // the material and the boundary conditions instead. The distinction the wording has
  // to carry is between asking ONCE, up front, about what only the user knows, and
  // asking permission at every step. So it invites the first and still refuses the
  // second.
  msg +=
    `\n\nThe canvas is empty. Set up the whole workflow that produces what I need ` +
    `and start it running. Ask me anything unclear in one go first, then don't ` +
    `check in at each step.`;
  return msg;
}
